import React from "react";

import { DatePicker, IDatePickerStrings } from "@nuorder/components";
import { formatMessage } from "lib/globalize-util";
import { MarketSettingsData } from "./market-settings.types";
import moment from "moment";
import { dateFormat, getDatePickerString } from "helpers/dates";
import { DATATEST } from "constants/integration";

interface IOwnProps {
    readonly?: boolean;
    assormentSettings: MarketSettingsData;
    setAssormentSettings: (MarketSettingsData) => void;
}

const styles = {
    row: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    datepicker: {
        root: {
            width: "48%",
        },
        textField: {
            fieldGroup: {
                border: "none",
            },
        },
    },
};

const formatDate = (date?: Date): string => (!date ? "" : moment(date).format(dateFormat.LONG_DATE_FORMAT));

export const MarketSettingsDates: React.FC<IOwnProps> = ({ readonly, assormentSettings, setAssormentSettings }) => {
    const dayPickerStrings: IDatePickerStrings = React.useMemo(() => getDatePickerString(), []);

    const handleUpdateDateField = React.useCallback(
        (field: keyof MarketSettingsData) => (value?: Date) => {
            setAssormentSettings((prev) => ({
                ...prev,
                [field]: value,
            }));
        },
        [setAssormentSettings],
    );

    const handleParseDateFromString = React.useCallback(
        (field: keyof MarketSettingsData) =>
            (newValue: string): Date => {
                const previousValue = (assormentSettings[field] as Date) || new Date();
                const newDateValue = moment(newValue, dateFormat.LONG_DATE_FORMAT, true);

                return newDateValue.isValid() ? newDateValue.toDate() : previousValue;
            },
        [assormentSettings],
    );

    return (
        <div style={styles.row}>
            <DatePicker
                placeholder={formatMessage("Select a date")}
                ariaLabel={formatMessage("Select a date")}
                textField={{ label: formatMessage("Market Date") }}
                onSelectDate={handleUpdateDateField("marketDate")}
                parseDateFromString={handleParseDateFromString("marketDate")}
                formatDate={formatDate}
                strings={dayPickerStrings}
                value={assormentSettings.marketDate}
                isMonthPickerVisible={false}
                disabled={readonly}
                allowTextInput
                data-test={DATATEST.ASSORTMENTS.ASSORMENT_SETTINGS.MARKET_DATE}
                styles={styles.datepicker}
            />
            <DatePicker
                placeholder={formatMessage("Select a date")}
                ariaLabel={formatMessage("Select a date")}
                textField={{ label: formatMessage("Order Due Date") }}
                onSelectDate={handleUpdateDateField("dueDate")}
                parseDateFromString={handleParseDateFromString("dueDate")}
                formatDate={formatDate}
                strings={dayPickerStrings}
                value={assormentSettings.dueDate}
                isMonthPickerVisible={false}
                disabled={readonly}
                allowTextInput
                data-test={DATATEST.ASSORTMENTS.ASSORMENT_SETTINGS.DUE_DATE}
                styles={styles.datepicker}
            />
        </div>
    );
};
